/**
 * The shared skeleton of a content page: frame, states, and the one async
 * mount every page in this directory is built on.
 *
 * SCOPE. This is structure, not decoration — a readable measure, a real
 * heading order, 44px tap targets, safe-area padding, and honest loading /
 * empty / not-found / error states. The visual pass comes later; what must not
 * come later is a page that says "loading" forever because a request failed,
 * or one that renders a spinner into a screen the user already left.
 *
 * THE STATE RULES, stated once here so the four pages do not each invent them:
 *  - LOADING is announced (`role="status"`, `aria-live="polite"`), so a screen
 *    reader is told something is happening rather than meeting silence.
 *  - NOT FOUND is a distinct state from ERROR. A 404 means the document does
 *    not exist; anything else means we could not tell. Rendering "not found"
 *    for a 500 tells the user a lie about our own availability.
 *  - PREVIEW is a third distinct state. In a preview build the backend answers
 *    404 for legal pages and empty for FAQ and news BY DESIGN, so the page
 *    says the feature is switched off in this build instead of claiming the
 *    document was deleted.
 *  - ERROR offers a retry, because most of them are transient, and uses
 *    `tError()` so the message is the server's own, translated.
 *  - An ABORTED request renders nothing. The screen is gone.
 */
import { t, tError } from '@/engine/i18n';
import { buttonClass } from '@/engine/ui/button';
import { createSlotIcon, type IconSlot } from '@/engine/ui/icon-registry';
import { attachPress } from '@/engine/ui/press';
import { isPreviewBuild } from '@/core/preview/preview';
import { isAbortError, isNotFoundError } from './cms-api';

/* ═══════════════════════════════════════════════════════════════ helpers ══ */

export function el<K extends keyof HTMLElementTagNameMap>(
  doc: Document,
  tag: K,
  className = '',
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = doc.createElement(tag);
  if (className !== '') node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/** Long-form reading column: ~70 characters, centred, safe-area aware.
 *  `rga-page-atmos` paints the misty rift backdrop (a fixed layer behind the
 *  column) so the document pages share the landing's atmosphere. */
const FRAME =
  'rga-page-atmos mx-auto w-full max-w-[46rem] px-4 pt-6 pb-16 ' +
  'ps-[max(1rem,env(safe-area-inset-left))] pe-[max(1rem,env(safe-area-inset-right))] ' +
  'pb-[max(4rem,env(safe-area-inset-bottom))]';

/** A link that is comfortably tappable on a phone. 44px is the HIG minimum. */
export const TAP_LINK = 'inline-flex items-center min-h-11 py-2 text-gold hover:underline';

/** The shared AAA skin (slate plate, rim, lip, press, gold focus ring) + layout. */
export const BUTTON = buttonClass('secondary', 'md', 'gap-2 px-4 no-underline');

/** Lead a control with an icon slot and give it the press-and-spring.
 *  `onPress` omitted = feedback only (an anchor's own navigation activates). */
export function pressable<T extends HTMLElement>(
  node: T,
  icon: IconSlot | null,
  onPress?: () => void,
  iconEnd = false,
): T {
  if (icon !== null) {
    const mark = createSlotIcon(node.ownerDocument, icon, { size: 18, class: 'rga-btn__icon' });
    if (iconEnd) node.append(mark);
    else node.prepend(mark);
  }
  attachPress(node, { onPress: onPress ?? (() => undefined) });
  return node;
}

export interface PageFrame {
  /** The `<main>` to mount. */
  readonly main: HTMLElement;
  /** Where a page puts its content; states are swapped in and out of here. */
  readonly body: HTMLElement;
}

/**
 * A `<main>` with the page's single `<h1>`, an optional back link above it,
 * and an empty body.
 *
 * `tabindex="-1"` on `<main>` so a "skip to content" link, or a router that
 * moves focus after a navigation, has somewhere to land — without it, keyboard
 * focus stays on the link that was clicked on the previous page.
 */
export function createPageFrame(
  doc: Document,
  options: {
    readonly title: string;
    readonly subtitle?: string;
    readonly backHref?: string;
    readonly backLabel?: string;
  },
): PageFrame {
  const main = el(doc, 'main', FRAME);
  // `main`, NOT `main-content`. The site header's skip link targets `#main` and
  // the footer's "back to top" moves focus to `#main`; this file shipped with a
  // different id, so on every content page the skip link had nowhere to go and
  // back-to-top silently failed to move focus. One id, spelled the same in all
  // four places (`layout/header.ts`, `layout/footer.ts`, `layout/routes.ts`,
  // `landing/landing-page.ts`).
  main.id = 'main';
  main.tabIndex = -1;

  if (options.backHref !== undefined && options.backLabel !== undefined) {
    // A single link is not a navigation landmark. Wrapping it in <nav> would
    // add a region to the landmark list that needs a name, and the only names
    // available ("Main navigation", "Footer navigation") would both be lies.
    const wrap = el(doc, 'p', 'mb-2');
    const back = el(doc, 'a', `${TAP_LINK} gap-1.5`, options.backLabel);
    back.href = options.backHref;
    pressable(back, 'header-back');
    wrap.appendChild(back);
    main.appendChild(wrap);
  }

  const h1 = el(doc, 'h1', 'font-heading text-2xl sm:text-3xl text-gold mb-2', options.title);
  main.appendChild(h1);

  if (options.subtitle !== undefined) {
    main.appendChild(el(doc, 'p', 'text-text-dim mb-6', options.subtitle));
  }

  const body = el(doc, 'div', 'mt-4');
  main.appendChild(body);
  return { main, body };
}

/* ════════════════════════════════════════════════════════════════ states ══ */

export function renderLoading(host: HTMLElement): void {
  const box = el(host.ownerDocument, 'div', 'py-8 text-text-dim', t('common.loading'));
  box.setAttribute('role', 'status');
  box.setAttribute('aria-live', 'polite');
  box.setAttribute('aria-label', t('a11y.loading_region'));
  host.replaceChildren(box);
}

export function renderEmpty(host: HTMLElement, message: string): void {
  host.replaceChildren(el(host.ownerDocument, 'p', 'py-8 text-text-dim', message));
}

/**
 * A 404, or — in a preview build — the deliberate absence that a preview
 * build serves in its place.
 *
 * `PREVIEW_BUILD=true` makes core answer 404 for every legal page and empty
 * for FAQ and news. That is a configuration decision, not a missing document,
 * and telling the visitor "this document does not exist" would be false.
 */
export function renderNotFound(
  host: HTMLElement,
  strings: { readonly title: string; readonly body: string },
): void {
  const doc = host.ownerDocument;
  const box = el(doc, 'div', 'py-8');
  box.setAttribute('role', 'status');

  if (isPreviewBuild()) {
    box.appendChild(el(doc, 'h2', 'text-lg mb-2', t('errors.http.503')));
    box.appendChild(el(doc, 'p', 'text-text-dim', t('errors.code.preview_disabled')));
  } else {
    box.appendChild(el(doc, 'h2', 'text-lg mb-2', strings.title));
    box.appendChild(el(doc, 'p', 'text-text-dim', strings.body));
  }
  host.replaceChildren(box);
}

export function renderError(host: HTMLElement, err: unknown, onRetry: () => void): void {
  const doc = host.ownerDocument;
  const box = el(doc, 'div', 'py-8');
  box.setAttribute('role', 'alert');
  box.setAttribute('aria-label', t('a11y.error_region'));
  box.appendChild(el(doc, 'p', 'mb-4 text-error', tError(err)));

  const retry = el(doc, 'button', BUTTON, t('common.retry'));
  retry.type = 'button';
  pressable(retry, 'action-retry', onRetry);
  box.appendChild(retry);

  host.replaceChildren(box);
}

/* ═══════════════════════════════════════════════════════════ async mount ══ */

export interface AsyncPageOptions<T> {
  /** Fetch. Must pass `signal` to the API so unmounting cancels it. */
  readonly load: (signal: AbortSignal) => Promise<T>;
  /** Draw the loaded data into the body. */
  readonly render: (data: T, body: HTMLElement) => void;
  /** Strings for the 404 state. */
  readonly notFound: { readonly title: string; readonly body: string };
  /** Optional: treat a successful-but-absent result as a 404. */
  readonly isMissing?: (data: T) => boolean;
}

/**
 * Run one page's load/render cycle with all four states and a working
 * teardown.
 *
 * The teardown aborts the in-flight request AND flips `live`, so a response
 * that arrives during unmount cannot write into a detached body — the class of
 * bug that shows the previous page's content for a frame after a navigation.
 */
export function mountAsyncPage<T>(body: HTMLElement, options: AsyncPageOptions<T>): () => void {
  let controller: AbortController | null = null;
  let live = true;

  const run = (): void => {
    controller?.abort();
    controller = new AbortController();
    const signal = controller.signal;
    renderLoading(body);

    options
      .load(signal)
      .then((data) => {
        if (!live || signal.aborted) return;
        if (options.isMissing?.(data) === true) {
          renderNotFound(body, options.notFound);
          return;
        }
        body.replaceChildren();
        options.render(data, body);
      })
      .catch((err: unknown) => {
        if (!live || isAbortError(err, signal)) return;
        if (isNotFoundError(err)) {
          renderNotFound(body, options.notFound);
          return;
        }
        renderError(body, err, run);
      });
  };

  run();

  return () => {
    live = false;
    controller?.abort();
    controller = null;
  };
}
